import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSessionBootstrap } from "./SessionBootstrapProvider";

type ProtectedRouteStatus = "checking" | "authenticated" | "redirecting" | "error";

export function useProtectedRouteSession(tenantSlug: string) {
  const location = useLocation();
  const navigate = useNavigate();
  const { status, session, errorMessage, verifySession } = useSessionBootstrap();
  const [routeStatus, setRouteStatus] = useState<ProtectedRouteStatus>("checking");

  useEffect(() => {
    let cancelled = false;

    async function checkSession() {
      setRouteStatus("checking");

      const result = await verifySession(tenantSlug);

      if (cancelled) {
        return;
      }

      if (result.status === "authenticated") {
        setRouteStatus("authenticated");
        return;
      }

      if (result.status === "unauthenticated") {
        setRouteStatus("redirecting");
        const returnTo = `${location.pathname}${location.search}`;
        navigate(`/t/${tenantSlug}/entry?returnTo=${encodeURIComponent(returnTo)}`, { replace: true });
        return;
      }

      setRouteStatus("error");
    }

    void checkSession();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, location.search, navigate, tenantSlug, verifySession]);

  return {
    routeStatus,
    sessionStatus: status,
    session,
    errorMessage,
    isSessionReady: routeStatus === "authenticated" && status === "authenticated"
  };
}
